/**
 * Утилиты для построения цветовой заливки карты по числовому значению
 * внутри диапазона доля страны-партнёра в экспорте/импорте.
*/

/** Базовый цвет в формате HSL. */
export type Hsl = {
  h: number;
  s: number;
  l: number;
};

export type ValueRange = {
  min: number;
  max: number;
};

/**
 * Границы яркости градиента:
 * pale — для минимума, deep — для максимума.
 */
export type Lightness = {
  pale: number;
  deep: number;
};

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

// Цвет, указанный в тз, отображается на 10% шкалы
const BASE_COLOR_POSITION = 0.1;

const PALE_SATURATION = 45;

/**
 * Парсит долю из локализованной строки в число.
 * Принимает форматы вида "45,2", "< 0,1 %"; возвращает null, если распарсить не удалось.
 *
 * @param value — исходное значение (строка/число/null)
 * @returns число доли или null
 */
export const parsePercent = (value: string | number | null | undefined): number | null => {
  if (value == null) return null;

  const parsed = parseFloat(
    String(value)
      .replace(',', '.')
      .replace(/[^\d.]/g, ''),
  );

  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Возвращает диапазон min, max по массиву чисел.
 * Для пустого массива возвращает { min: 0, max: 0 }.
 *
 * @param values — массив числовых значений
 */
export const getValuesRange = (values: number[]): ValueRange => {
  if (!values.length) return { min: 0, max: 0 };
  return { min: Math.min(...values), max: Math.max(...values) };
};

/**
 * Вычисляет HSL-цвет для значения внутри диапазона.
 * Минимум диапазона — самый бледный оттенок, максимум — самый насыщенный,
 * середина соответствует базовой светлоте baseHsl.l.
 * При (min === max) возвращает базовый цвет
 *
 * @param value — значение, для которого нужен цвет
 * @param baseHsl — базовый цвет потока
 * @param range — диапазон значений
 * @param lightness — границы светлоты градиента (pale — минимум, deep — максимум)
 * @returns строка вида hsl(207, 67%, 46%)
 */
export const getGradientColor = (value: number, baseHsl: Hsl, range: ValueRange, lightness: Lightness): string => {
  const { pale, deep } = lightness;

  if (!range || range.max === range.min) {
    return `hsl(${baseHsl.h}, ${baseHsl.s}%, ${baseHsl.l}%)`;
  }

  const ratio = clamp((value - range.min) / (range.max - range.min), 0, 1);

  let resultLightness: number;
  let resultSaturation = baseHsl.s;

  if (ratio <= BASE_COLOR_POSITION) {
    const t = ratio / BASE_COLOR_POSITION; 
    resultLightness = pale - (pale - baseHsl.l) * t;
    resultSaturation = PALE_SATURATION + (baseHsl.s - PALE_SATURATION) * t;
  } else {
    resultLightness = baseHsl.l - (baseHsl.l - deep) * ((ratio - BASE_COLOR_POSITION) / (1 - BASE_COLOR_POSITION));
  }

  return `hsl(${baseHsl.h}, ${Math.round(resultSaturation)}%, ${Math.round(resultLightness)}%)`;
};

/**
 * Собирает CSS linear-gradient по остановкам stops (значения 0..1).
 * Цвет каждой остановки берётся из {@link getGradientColor}.
 *
 * @param baseHsl — базовый цвет потока
 * @param range — диапазон значений
 * @param stops — доли значения для выбора цвета остановок, 0..1
 * @param lightness — границы светлоты градиента
 * @param leadColor — цвет начала полосы (например, «нет данных»); заменяет цвет первой остановки
 * @param positions — доли ширины полосы для каждой остановки, 0..1 (по умолчанию равны stops).
 *   Позволяет растянуть/сжать часть шкалы в легенде, не меняя сам цвет значения.
 * @returns строка linear-gradient(90deg, ...)
 */
export const buildGradientCss = (
  baseHsl: Hsl,
  range: ValueRange,
  stops: number[],
  lightness: Lightness,
  leadColor?: string,
  positions?: number[],
): string => {
  const colorStops = stops.map((ratio, index) => {
    const value = range.min + (range.max - range.min) * ratio;
    const color = leadColor && index === 0 ? leadColor : getGradientColor(value, baseHsl, range, lightness);
    const position = positions ? positions[index] : ratio;
    return `${color} ${position * 100}%`;
  });

  return `linear-gradient(90deg, ${colorStops.join(', ')})`;
};

/**
 * Формирует подписи легенды: минимум, середина и максимум диапазона.
 *
 * @param range — диапазон значений
 * @param format — форматтер значения в строку (например, numberFormatter компонента)
 * @returns массив из трёх подписей
 */
export const buildRangeMarks = (range: ValueRange, format: (value: number) => string): string[] =>
  [range.min, (range.min + range.max) / 2, range.max].map((value) => format(value));
